import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, List, Calendar, Settings, LogOut, Mail, CreditCard, FileSpreadsheet } from "lucide-react";
import {
  ADMIN_DASHBOARD_URL, ADMIN_BOOKINGS_URL, ADMIN_CALENDAR_URL,
  ADMIN_PAYMENTS_URL, ADMIN_WAITLIST_URL, ADMIN_SETTINGS_URL,
  ADMIN_REPORTS_URL,
  ADMIN_LOGIN_URL,
} from "@/constants/adminRoutes";

const navItems = [
  { to: ADMIN_DASHBOARD_URL, icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: ADMIN_BOOKINGS_URL, icon: List, label: "Bookings" },
  { to: ADMIN_CALENDAR_URL, icon: Calendar, label: "Calendar" },
  { to: ADMIN_PAYMENTS_URL, icon: CreditCard, label: "Payments" },
  { to: ADMIN_WAITLIST_URL, icon: Mail, label: "Waitlist" },
  { to: ADMIN_REPORTS_URL, icon: FileSpreadsheet, label: "Reports" },
  { to: ADMIN_SETTINGS_URL, icon: Settings, label: "Security" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate(ADMIN_LOGIN_URL);
  };

  return (
    <div className="min-h-screen bg-sarathi-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-56 bg-sarathi-surface border-r border-white/5 flex-col shrink-0">
        <div className="p-4 border-b border-white/5">
          <img src="https://customer-assets.emergentagent.com/job_ev-mobility-2/artifacts/46iqyr35_logo-orange-white.svg" alt="Sarathi-X" className="h-7" />
          <p className="text-sarathi-text-muted text-xs mt-1">Admin Panel</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-sarathi-primary/15 text-sarathi-primary" : "text-sarathi-text-secondary hover:text-white hover:bg-white/5"}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/5">
          <div className="text-xs text-sarathi-text-muted mb-2 px-3 truncate">{user?.email}</div>
          <button
            type="button"
            onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-sarathi-text-secondary hover:text-white hover:bg-white/5 w-full" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-sarathi-surface border-b border-white/5">
          <img src="https://customer-assets.emergentagent.com/job_ev-mobility-2/artifacts/46iqyr35_logo-orange-white.svg" alt="Sarathi-X" className="h-6" />
          <div className="flex gap-2">
            {navItems.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({ isActive }) => `p-2 rounded-lg ${isActive ? "bg-sarathi-primary/15 text-sarathi-primary" : "text-sarathi-text-secondary"}`}>
                <item.icon className="w-5 h-5" />
              </NavLink>
            ))}
            <button
              type="button"
              onClick={handleLogout} className="p-2 text-sarathi-text-secondary"><LogOut className="w-5 h-5" /></button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
